import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  Card,
  CardMedia,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { ZegoUIKitPrebuilt } from "@zegocloud/zego-uikit-prebuilt";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import Select from "../../../../components/Select/Select";
import { soapApiJson } from "../../../../redux/actions/api";
import {
  clearInpatientAlerts,
  clinicReassignAppointment,
  clinicRescheduleAppointment,
  getAppointmentsByVetIdAndDate,
  getAvailabilityByVetIdAndType,
  getInpatientAlertsByAppointmentId,
  getInpatientAlertsByAppointmentIdWoLoader,
  getInpatientTasksByAppointmentId,
  postClinicalNotesWoLoader,
  setApiInProgress,
  updateAppointmentAssignedUsers,
  updateAppointmentStatusById,
  updateCancel,
  updateHospitalizationStatus,
  updateInpatientNoteReviewed,
  updateInpatientRedAlerts,
  updatePatientTaskStatusById,
} from "../../../../redux/reducers/clinicSlice";
import { getVetHolidaysById } from "../../../../redux/reducers/doctorSlice";
import {
  hideLoader,
  showLoader,
  showLoaderText,
} from "../../../../redux/reducers/loaderSlice";
import {
  getPetAndAppointmentDetailsById,
  getPetPrescriptionsWoLoader,
} from "../../../../redux/reducers/petSlice";
import { assignBundleToAppointment } from "../../../../redux/reducers/planSlice";
import { showSnackBar } from "../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../util/AppColors";
import { movePatientList } from "../../../../util/arrayList";
import { inpatFieldMap } from "../../../../util/arrayMap";
import {
  getClinicTemperatureLabel,
  getClinicWeightLabel,
} from "../../../../util/clinicMeasurementUnits";
import { addIcon } from "../../../../util/CommonIconStyle";
import {
  appointmentLaterList,
  appointmentList,
} from "../../../../util/dropList";
import {
  adjustTime,
  extractPrescriptionDetails,
  getCardColorByApptStatus,
  getDayOffsetNumber,
  getRequiredSlotList,
  getRightSaluation,
  getTimeDiffInMins,
  getTypeByAppointmentType,
} from "../../../../util/function";
import { canWriteModule } from "../../../../util/roleModuleAccess";
import BookAnAppointment from "../../../CommonScreens/BookAppointmentment/BookAnAppointment";
import UpdateRemarks from "../../../CommonScreens/UpdateRemarks/UpdateRemarks";
import {
  getPrescriptionsFetchKey,
  setPrescriptionsFetchKey,
} from "../clinicPetDetailsSession";
import "../IOMonitoring/IOMonitoring.css";
import { initialVitals } from "../IOMonitoring/mockIOMonitoringData";
import "../TreatmentPlan/TreatmentPlan.css";
import Instructions from "./Instructions";
import OverviewDiagnostics from "./OverviewDiagnostics";

const TASKS_PREVIEW_LIMIT = 3;

const getScheduleStatus = (task) => {
  if (String(task?.status) === "true") return "GIVEN";
  if (String(task?.status) === "false") return "NOT_DONE";
  const [hour, min] = String(task?.startTime || "00:00").split(":");
  const taskDateTime = new Date();
  taskDateTime.setHours(Number(hour) || 0, Number(min) || 0, 0, 0);
  const isTimeExceed = new Date().getTime() > taskDateTime.getTime();
  return isTimeExceed ? "OVERDUE" : "DUE";
};

const cardSty = {
  borderRadius: 1,
  padding: 2,
  borderTopColor: AppColors.appPrimary,
};

const inpatientList = [
  "Summary",
  "Reminders",
  "InstructionsStatus",
  "AbnormalFindings",
  // "KeyIssues",
  "RedFlags",
  "OtherUpdates",
  "Observation",
  "ProgressNotes",
  "Conclusion",
  "Protocols",
];

const nameExten = {
  Summary: "Summary",
  Reminders: "Reminders",
  InstructionsStatus: "Instructions Status",
  AbnormalFindings: "Abnormal Findings",
  KeyIssues: "Key Issues",
  RedFlags: "Red Flags",
  OtherUpdates: "Other Updates",
  Observation: "Observation",
  ProgressNotes: "Progress Notes",
  Conclusion: "Conclusion",
  Protocols: "Protocols",
};

const pickVitalField = (data = {}, keys = []) => {
  const entries = Object.entries(data ?? {});
  for (const key of keys) {
    const match = entries.find(
      ([k]) => String(k).trim().toLowerCase() === key.toLowerCase(),
    );
    if (match?.[1] != null && String(match[1]).trim() !== "") {
      return String(match[1]).trim();
    }
  }
  for (const key of keys) {
    const match = entries.find(([k]) =>
      String(k).trim().toLowerCase().includes(key.toLowerCase()),
    );
    if (match?.[1] != null && String(match[1]).trim() !== "") {
      return String(match[1]).trim();
    }
  }
  return "";
};

const withUnit = (value, unit) => {
  if (!value) return "";
  if (!unit) return value;
  const lower = value.toLowerCase();
  if (lower.includes(unit.toLowerCase().replace("°", ""))) return value;
  return `${value}${unit.startsWith("°") || unit.startsWith("/") ? "" : " "}${unit}`;
};

const buildLatestVitalsSummary = (vitalsList = [], appointmentWeight) => {
  const latest = [...(vitalsList ?? [])]?.sort(
    (a, b) =>
      new Date(b?.updatedDate || b?.createdDate) -
      new Date(a?.updatedDate || a?.createdDate),
  )?.[0];
  const data = latest?.data?.[0] ?? {};
  const asOf = latest?.updatedDate || latest?.createdDate;
  const asOfTime = asOf
    ? `as of ${moment(new Date(asOf)).format("hh:mm a")}`
    : initialVitals.asOfTime;

  const weight = withUnit(
    pickVitalField(data, ["body weight", "weight"]) ||
      (appointmentWeight != null && appointmentWeight !== ""
        ? String(appointmentWeight)
        : ""),
    getClinicWeightLabel(),
  );
  const temperature = withUnit(
    pickVitalField(data, ["body temperature", "temperature", "temp"]),
    getClinicTemperatureLabel(),
  );
  const heartRate = withUnit(
    pickVitalField(data, ["heart rate", "pulse", "hr"]),
    "bpm",
  );
  const respiratoryRate = withUnit(
    pickVitalField(data, ["respiratory rate", "respiration", "resp", "rr"]),
    "rpm",
  );
  const bodyConditionScore = pickVitalField(data, [
    "body condition score",
    "body condition",
    "bcs",
  ]);

  const hasAny =
    weight || temperature || heartRate || respiratoryRate || bodyConditionScore;

  if (!hasAny) {
    return {
      weight: "—",
      temperature: "—",
      heartRate: "—",
      respiratoryRate: "—",
      bodyConditionScore: "—",
      asOfTime: initialVitals.asOfTime,
    };
  }

  return {
    weight: weight || "—",
    temperature: temperature || "—",
    heartRate: heartRate || "—",
    respiratoryRate: respiratoryRate || "—",
    bodyConditionScore: bodyConditionScore || "—",
    asOfTime,
  };
};

const isAbnormalVital = (key, value) => {
  if (!value || value === "—") return false;
  const num = parseFloat(String(value).replace(/[^\d.]/g, ""));
  if (!Number.isFinite(num)) return false;
  if (key === "temperature") {
    // Support °C (workbook) and legacy °F readings
    return num >= 100 ? num >= 103 : num >= 39.5;
  }
  return false;
};

const Overview = ({
  // getPetData,
  isBaseDetailsLoaded,
  isVideoCallEnabled,
  setVideoCallEnabled,
  setAllergyModOpen,
  appointment,
  selectedTab,
  setSelectedTab,
}) => {
  const dispatch = useDispatch();
  const canManageDashboard = canWriteModule("dashboard");
  const location = useLocation();
  const navigate = useNavigate();
  const [isEditAppointment, setEditAppointment] = useState(false);
  const [cancelModVsble, setCancelModVsble] = useState(false);
  const [noShowModVsble, setNoshowModVsble] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reModVsble, setReModVsble] = useState(false);
  const [modValues, setModValues] = useState({});
  const [errors, setErrors] = useState({});
  const [vets, setVets] = useState([]);
  const [followUpModVsble, setFollowUpModVsble] = useState(false);
  const [selectTimeIdError, setSelectTimeIdError] = useState(false);
  const [remarksModVsble, setRemarksModVsble] = useState(false);
  const [taskCancelModVsble, setTaskCancelModVsble] = useState(false);
  const [taskCancelRemarks, setTaskCancelRemarks] = useState("");
  const [selectedScheduleTask, setSelectedScheduleTask] = useState(null);
  const [selectTime, setSelectTime] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleSearchValue, setBundleSearchValue] = useState("");
  const [bundleModVsble, setBundleModVsble] = useState(false);
  const [movPatModVsble, setMovPatModVsble] = useState(false);
  const [redAlertModVsble, setRedAlertModVsble] = useState(false);
  const [redFlagUpdatesVsble, setRedFlagUpdatesVsble] = useState(false);
  const [dailyUpdatesVsble, setDailyUpdatesVsble] = useState(false);
  const [assignUsersModVsble, setAssignUsersModVsble] = useState(false);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const { petDetails, prescriptions, procedure } = useSelector(
    (state) => state?.pet,
  );
  const medHisList = useSelector(
    (state) => state?.medicalHistory?.medicalHistoryByPet,
  );
  const { petTreatmentBundles } = useSelector((state) => state?.plan);
  const {
    vetAvailability,
    vetBookedApnts,
    userDetails,
    details: clinicDetails,
    inpatientAlerts,
    inpatientTaskList,
  } = useSelector((state) => state?.clinic);
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  // const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const holidays = useSelector((state) => state?.doctor?.holidays);
  const apntId =
    appointment?.appoinment?.appointmentId ??
    petAndApntData?.appointment?.appointmentId;
  const appDet = petAndApntData?.appointment;
  const inpatData = petAndApntData?.inpatientResponse ?? {};
  const inpatientStatusKey = String(
    inpatData?.inpatientStatus || "",
  ).toLowerCase();
  const inpatientStatusClass =
    inpatientStatusKey === "critical"
      ? "red-back2"
      : inpatientStatusKey === "monitoring"
        ? "card-bot-yellow-back"
        : inpatientStatusKey === "isolation"
          ? "purple-back"
          : "green-back5";
  const petId =
    appointment?.appoinment?.petId ??
    petAndApntData?.pet?.petId ??
    petAndApntData?.appointment?.petId;
  const appointmentId = petAndApntData?.appointment?.appointmentId;
  const petData = petAndApntData?.pet;
  const petDet = petAndApntData?.pet;
  const apnt = petAndApntData?.appointment ?? appointment?.appoinment;

  const latestVitalsSummary = useMemo(
    () =>
      buildLatestVitalsSummary(
        reqDet?.vitals ?? [petAndApntData?.vitals],
        appDet?.weight ?? petAndApntData?.appointment?.weight,
      ),
    [
      reqDet?.vitals,
      petAndApntData?.vitals,
      appDet?.weight,
      petAndApntData?.appointment?.weight,
    ],
  );

  const goToIoMonitoring = () => {
    navigate(`/clinic-pet-details/patient-workbook`, {
      state: {
        ...(location.state || {}),
        selectedTab: "patientWorkbook",
        workbookSubTab: "Vitals",
      },
    });
  };

  const goToTreatmentPlanSchedule = () => {
    navigate(`/clinic-pet-details/patient-workbook`, {
      state: {
        ...(location.state || {}),
        selectedTab: "patientWorkbook",
        workbookSubTab: "Treatment Plan",
      },
    });
  };

  const overviewScheduleEntries = useMemo(() => {
    const today = moment().format("YYYY-MM-DD");
    return (inpatientTaskList ?? [])
      ?.filter((li) => li?.taskType?.toLowerCase() !== "main")
      ?.filter((li) => li?.startDate === today)
      ?.filter((li) => li?.active !== false)
      ?.map((task) => ({
        id: task.id,
        time: moment(`${task.startDate}T${task.startTime}`).isValid()
          ? moment(`${task.startDate}T${task.startTime}`).format("hh:mm a")
          : task.startTime || "—",
        name: task.task || "—",
        status: getScheduleStatus(task),
        raw: task,
      }))
      ?.sort((a, b) =>
        String(a.raw?.startTime || "").localeCompare(
          String(b.raw?.startTime || ""),
        ),
      );
  }, [inpatientTaskList]);

  const overdueTasks = useMemo(
    () =>
      (overviewScheduleEntries ?? []).filter(
        (entry) => entry.status === "OVERDUE",
      ),
    [overviewScheduleEntries],
  );

  const upcomingTasks = useMemo(
    () =>
      (overviewScheduleEntries ?? []).filter((entry) => entry.status === "DUE"),
    [overviewScheduleEntries],
  );

  const markTaskGiven = (entry) => {
    const taskId = entry?.raw?.id ?? entry?.id;
    if (!taskId) return;
    dispatch(
      updatePatientTaskStatusById({
        taskId,
        url: `?status=${true}&remark=`,
      }),
    ).then((res) => {
      if (res?.payload && appointmentId) {
        dispatch(getInpatientTasksByAppointmentId(appointmentId));
      }
    });
  };

  const markTaskNotDone = (entry, remarks = "") => {
    const taskId = entry?.raw?.id ?? entry?.id;
    if (!taskId) return;
    dispatch(
      updatePatientTaskStatusById({
        taskId,
        url: `?status=${false}&remark=${remarks}`,
      }),
    ).then((res) => {
      if (res?.payload && appointmentId) {
        dispatch(getInpatientTasksByAppointmentId(appointmentId));
      }
    });
  };

  const handleTaskCancel = (entry) => {
    setSelectedScheduleTask(entry);
    setTaskCancelModVsble(true);
  };

  const onCloseTaskCancelMod = () => {
    setTaskCancelModVsble(false);
    setTaskCancelRemarks("");
    setSelectedScheduleTask(null);
  };

  const onSaveTaskCancelRemarks = () => {
    markTaskNotDone(selectedScheduleTask, taskCancelRemarks);
    onCloseTaskCancelMod();
  };

  const statusActive =
    typeof petData?.status === "boolean"
      ? petData?.status
      : petData?.status === "Y";
  const user = localStorage.getItem("user");
  const role = localStorage.getItem("role");
  const profile = JSON.parse(user);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { vets: clinicVets } = useMemo(() => {
    if (!mandateDataRaw) {
      return { vets: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        vets: Array.isArray(reqData?.vets) ? reqData?.vets : [],
      };
    } catch {
      return { vets: [] };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    if (selectedTab !== "overView" || !petId || !isBaseDetailsLoaded) return;
    const fetchKey = `${petId}`;
    if (getPrescriptionsFetchKey() === fetchKey) return;
    setPrescriptionsFetchKey(fetchKey);
    dispatch(
      getPetPrescriptionsWoLoader({
        petId,
        url: "?page=1&itemSize=5",
      }),
    );
  }, [selectedTab, petId, dispatch, isBaseDetailsLoaded]);

  useEffect(() => {
    if (selectedTab !== "overView" || !inpatData?.patientId || !appointmentId)
      return;
    dispatch(getInpatientAlertsByAppointmentIdWoLoader(appointmentId));
    return () => {
      dispatch(clearInpatientAlerts());
    };
  }, [
    selectedTab,
    inpatData?.patientId,
    appointmentId,
    location.key,
    dispatch,
  ]);

  useEffect(() => {
    if (selectedTab !== "overView" || !appDet?.inpatient || !appointmentId)
      return;
    dispatch(getInpatientTasksByAppointmentId(appointmentId));
  }, [selectedTab, appDet?.inpatient, appointmentId, dispatch]);

  useEffect(() => {
    const reqVetList = clinicVets
      ?.filter((vl) => {
        if (
          vl?.status === "Y" &&
          vl?.userType?.some(
            (ut) => ut?.name === "ROLE_DOCTOR" || ut?.name === "ROLE_GROOMER",
          )
        ) {
          return vl;
        }
      })
      ?.map((vet) => {
        return { name: vet?.name, value: vet?.doctorId, id: vet?.doctorId };
      });
    setVets(
      [
        {
          name: `${
            role === "ROLE_CLINIC" ? profile?.name : clinicDetails?.name
          } (Clinic)`,
          value: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
          id: role === "ROLE_CLINIC" ? profile?.id : clinicDetails?.id,
        },
      ].concat(reqVetList),
    );
  }, [clinicVets]);

  useEffect(() => {
    if (
      appDet?.appointmentStatus?.toLowerCase() !== "scheduled" ||
      !modValues?.vetName?.value ||
      !modValues?.date ||
      !modValues?.appointmentOption
    ) {
      return;
    }
    if (modValues?.appointmentOption === "Later") {
      dispatch(getVetHolidaysById(modValues?.vetName?.value));
      dispatch(
        getAvailabilityByVetIdAndType({
          vetId: modValues?.vetName?.value,
          type: modValues?.consultationMode,
        }),
      );
      dispatch(
        getAppointmentsByVetIdAndDate({
          vetId: modValues?.vetName?.value,
          date: moment(new Date(modValues?.date)).format("YYYY-MM-DD"),
          clinicId: profile?.id,
        }),
      );
    }
  }, [
    modValues?.vetName?.value,
    modValues?.date,
    modValues?.appointmentOption,
    modValues?.consultationMode,
  ]);

  const myMeeting = async (element) => {
    const roomID = appDet?.videoCallId;
    const userID = appDet?.doctorId?.toString();
    const userName = appDet?.doctorName;
    const token = "faeb80d20908d605f3acbf67f8c25191";
    const kitToken = ZegoUIKitPrebuilt.generateKitTokenForTest(
      704271205,
      token,
      roomID,
      userID,
      userName,
    );

    const zp = ZegoUIKitPrebuilt.create(kitToken);
    zp.joinRoom({
      container: element,
      scenario: {
        mode: ZegoUIKitPrebuilt.GroupCall,
      },
      onLeaveRoom: async () => {
        setVideoCallEnabled(false);
        localStorage.setItem(
          "videoCallPageReloaded",
          JSON.stringify({
            state: appDet,
          }),
        );
        window.location.reload();
      },
      showLeavingView: false,
      showScreenSharingButton: false,
      maxUsers: 2,
      showPreJoinView: false,
      showTextChat: false,
      showRoomDetailsButton: false,
    });
  };

  const getAppointmentSummaryImgaes = () => {
    return (
      medHisList?.find((med) => med?.appointentId === appDet?.appointmentId)
        ?.appointmentImage ?? null
    );
  };

  const handleBundleclose = () => {
    setSelectedBundle(null);
    setBundleModVsble(false);
    setBundleSearchValue("");
  };

  const handleAssign = () => {
    if (!selectedBundle?.bundleId || !apntId) return;
    dispatch(
      assignBundleToAppointment({
        bundleId: selectedBundle?.bundleId,
        appointmentId: apntId,
      }),
    ).then((res) => {
      if (res?.payload) {
        onApiCall();
        // dispatch(getPetsCompliantSummary(apntId));
        handleBundleclose();
      }
    });
  };

  const getRequiredPresData = () => {
    const getAllList = extractPrescriptionDetails(prescriptions?.data);
    const reqList = getAllList?.filter(
      (li) => new Date(li?.endDate) >= new Date(new Date().setHours(0, 0, 0)),
    );
    return reqList;
  };

  const onCancelModClose = () => {
    setCancelModVsble(false);
    setCancelReason("");
  };

  const onApiCall = () => {
    dispatch(
      getPetAndAppointmentDetailsById({
        petId,
        url: `${apntId ? `?appointmentId=${apntId}` : ""}`,
      }),
    );
    // dispatch(getPetsCompliantSummary(apntId));
  };

  const onConfirmCancel = () => {
    dispatch(
      updateCancel({
        appId: apntId,
        reason: cancelReason,
      }),
    ).then((res) => {
      if (res?.payload) {
        onCancelModClose();
        onApiCall();
      }
    });
  };

  const onReModClose = () => {
    setReModVsble(false);
    setModValues({});
    setErrors({});
    setSelectTime(null);
    setSelectTimeIdError(false);
  };

  const onChangeValue = (name, value) => {
    setModValues({ ...modValues, [name]: value });
    if (
      appDet?.appointmentStatus?.toLowerCase() === "consultation" ||
      appDet?.appointmentStatus?.toLowerCase() === "finalize" ||
      appDet?.appointmentStatus?.toLowerCase() === "completed"
    ) {
      const reqValue =
        typeof value === "object"
          ? typeof value?.value === "number"
            ? value?.value?.toString()
            : value?.value
          : value;
      setErrors({
        ...errors,
        [name]: reqValue?.length > 0 ? false : "Required Field",
      });
    }
  };

  const handleSelectTime = (time) => {
    setSelectTime(time?.startTime);
    setSelectTimeIdError(false);
  };

  const onSaveRe = () => {
    if (
      appDet?.appointmentStatus?.toLowerCase() === "scheduled" &&
      !selectTime &&
      modValues?.appointmentOption !== "Immediate"
    ) {
      return setSelectTimeIdError(true);
    }
    if (!validateFields()) return;
    if (appDet?.appointmentStatus?.toLowerCase() !== "scheduled") {
      if (!modValues?.vetName?.value) return;
      dispatch(showLoader());
      const data = { doctorId: modValues?.vetName?.value };
      if (modValues?.assignedUsers?.length > 0) {
        data.assignedUsers = modValues?.assignedUsers?.map((li) => li?.value);
      }
      // const form = new FormData();
      // form.append("doctorId", modValues?.vetName?.value);
      dispatch(clinicReassignAppointment({ appointmentId: apntId, data })).then(
        (res) => {
          if (res?.payload) {
            onReModClose();
            onApiCall();
          }
        },
      );
      dispatch(hideLoader());
    } else {
      dispatch(showLoader());
      const data = {
        appointmentDate: moment(new Date(modValues?.date)).format("YYYY-MM-DD"),
        doctorId: modValues?.vetName?.value,
        appointmentStartTime: selectTime,
      };
      data.appointmentEndTime = adjustTime(
        selectTime,
        getTimeDiffInMins(
          appDet?.appointmentStartTime,
          appDet?.appointmentEndTime,
        )?.totalMinutes ?? 0,
        // getRequiredSlotList({
        //   availability: vetAvailability,
        //   date: modValues?.date,
        //   isDuration: true,
        // })
      );
      dispatch(
        clinicRescheduleAppointment({ appointmentId: apntId, data }),
      ).then((res) => {
        if (res?.payload) {
          onReModClose();
          onApiCall();
        }
      });
      dispatch(hideLoader());
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!modValues?.vetName?.value)
      newErrors.vetName = "This field is required";
    if (!modValues?.appointmentOption)
      newErrors.appointmentOption = "Appointment Type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onHandleRe = () => {
    setModValues({
      ...modValues,
      appointmentTime: appDet?.appointmentTime ?? appDet?.appointmentStartTime,
      appointmentId: appDet?.appointmentId,
      appointmentDate: appDet?.appointmentDate,
      bookingType: "Walk-in",
      consultationMode: appDet?.appointmentType,
      serviceType: appDet?.serviceType,
      reason: appDet?.reason,
      vetName: vets?.find((vet) => vet?.value === appDet?.doctorId) ?? null,
      appointmentOption: appDet?.immediate ? "Immediate" : "Later",
      date: new Date(appDet?.appointmentDate),
    });
    setReModVsble(true);
  };

  const onCloseFollowUpMod = () => {
    setEditAppointment(false);
    setFollowUpModVsble(false);
    setModValues({});
    setSelectTime(null);
    setSelectTimeIdError(false);
    setErrors({});
  };

  const handleFollowUp = () => {
    setModValues({
      ...modValues,
      bookingType: "Phone",
      consultationMode: "Physical",
      reason: "",
      vetName: null,
      appointmentOption: "",
      date: new Date(),
      serviceType: { name: "Follow-up", value: "Follow-up" },
    });
    setFollowUpModVsble(true);
  };

  const handleEditRemarks = () => {
    setRemarksModVsble(true);
  };

  const onCloseRemarks = () => {
    setRemarksModVsble(false);
  };

  const handleMovePatinet = () => {
    setModValues({
      moveTo:
        !petAndApntData?.inpatient?.hospitalization ??
        movePatientList?.[0]?.value,
    });
    setMovPatModVsble(true);
  };

  const onCloseModPatMod = () => {
    setMovPatModVsble(false);
    setModValues({});
  };

  const onChangeMovePat = (name, value) => {
    setModValues({ ...modValues, [name]: value });
  };

  const onMovePatient = () => {
    if (!inpatData?.patientId) return;
    dispatch(
      updateHospitalizationStatus({
        patientId: inpatData?.patientId,
        status: modValues?.moveTo,
      }),
    ).then((res) => {
      if (res?.payload) {
        if (modValues?.moveTo === false) {
          dispatch(
            updateAppointmentStatusById({
              appointmentId: appointmentId,
              status: "scheduled",
              successMsg: "Status updated successfully!",
            }),
          ).then((res2) => {
            if (res2?.payload) {
              onApiCall();
              onCloseModPatMod();
            }
          });
          return;
        }
        onApiCall();
        onCloseModPatMod();
      }
    });
  };

  const handleAddUpdateRedAlert = (itm) => {
    setModValues({ alert: itm?.text ?? "", alertId: itm?.alertId ?? null });
    setRedAlertModVsble(true);
  };

  const onCloseRedAlertMod = () => {
    setRedAlertModVsble(false);
    setModValues({});
  };

  const onSaveRedAlert = () => {
    if (!inpatData?.patientId) return;
    const data = {
      inpatientId: inpatData?.patientId,
      doctorId: petAndApntData?.appointment?.doctorId,
      text: modValues?.alert,
    };
    if (modValues?.alertId) {
      data.alertId = modValues?.alertId;
    }
    dispatch(updateInpatientRedAlerts(data)).then((res) => {
      if (res?.payload) {
        onCloseRedAlertMod();
        if (apntId) {
          dispatch(getInpatientAlertsByAppointmentId(apntId));
        }
        // onApiCall();
      }
    });
  };

  function toObject(data) {
    try {
      if (typeof data === "string") {
        return JSON.parse(data);
      }
      if (typeof data === "object" && data !== null) {
        return data;
      }
      throw new Error("Unsupported data type");
    } catch (err) {
      console.error("Error converting to object:", err);
      return null;
    }
  }

  const getApptDetailsForSoap = () => {
    const reqObj = {
      petName: petDet?.petName ?? apnt?.petName ?? "",
      species: petDet?.petType ?? "",
      breed: petDet?.breed ?? apnt?.breed ?? "",
      age: petDet?.dob ?? apnt?.dob ?? "",
      reproductiveStatus: petDet?.reproductiveStatus ?? "",
      gender: petDet?.gender ?? apnt?.gender ?? "",
      weight: petDet?.weight ?? apnt?.weight ?? "",
      appointmentDateTime: apnt?.appointmentdateTime ?? "",
      bloodGroup: petDet?.bloodGroup ?? apnt?.bloodGroup ?? "",
      temperament: petDet?.temperament ?? apnt?.temperament ?? "",
      color: petDet?.color ?? apnt?.color ?? "",
      rfid: petDet?.rfid ?? apnt?.rfid ?? "",
      veterinaryClinic: clinicDetails?.name ?? "",
      username: petDet?.userName ?? apnt?.userName ?? "",
      vetName: apnt?.doctorName ?? "",
      appointmentId: apnt?.appointmentId ?? "",
    };
    return JSON.stringify(reqObj);
  };

  const onConsolidateSoap = async (typ = "inpatient") => {
    const reqList = petAndApntData?.inpatientNotes?.filter(
      (li) =>
        li?.appointmentId === appointmentId &&
        li?.type?.toLowerCase() === typ?.toLowerCase() &&
        !li?.isMaual &&
        !li?.consolidated,
    );
    if (reqList?.length === 0 || reqList?.length === 1) return;
    const soap_notes = reqList?.map((obj) =>
      inpatFieldMap
        ?.map(([k, src]) => {
          const raw = (obj[src] ?? "").toString();
          const safe = raw?.replace(/\r?\n/g, " ")?.replace(/'/g, "\\'");
          return `${k}:'${safe}'`;
        })
        ?.join(","),
    );
    dispatch(showLoader());
    dispatch(showLoaderText("Please wait while we consolidate the SOAP notes"));
    dispatch(
      setApiInProgress({ apiType: "consolidateSoap", inProgress: true }),
    );
    const res = await soapApiJson.post("/consolidate-soap-notes", {
      soap_notes,
      case_type: typ,
      appt_details: getApptDetailsForSoap(),
    });
    if (res?.status === 200) {
      dispatch(
        setApiInProgress({ apiType: "consolidateSoap", inProgress: false }),
      );
      if (res?.data?.consolidated_soap_note) {
        const reqObj = toObject(res?.data?.consolidated_soap_note);
        dispatch(hideLoader());
        dispatch(showLoaderText(""));
        const apiObj = {
          ...reqObj,
          sessionId: res?.data?.session_id ?? reqObj?.session_id ?? "",
          modificationCount:
            res?.data?.modification_count ?? reqObj?.modification_count ?? 0,
          consolidated: true,
          type: typ,
          Protocols: reqObj?.Protocols ?? "",
          ProgressNotes: reqObj?.ProgressNotes ?? "",
          Conclusion: reqObj?.Conclusion ?? "",
        };
        onUpdateNote(apiObj);
      } else {
        dispatch(
          setApiInProgress({ apiType: "consolidateSoap", inProgress: false }),
        );
        dispatch(hideLoader());
        dispatch(showLoaderText(""));
      }
    } else {
      dispatch(
        setApiInProgress({ apiType: "consolidateSoap", inProgress: false }),
      );
      dispatch(hideLoader());
      dispatch(showLoaderText(""));
    }
  };

  const onUpdateNote = ({
    Subjective = "",
    Objective = "",
    Assessment = "",
    Plan = "",
    Conclusion = "",
    DifferentialDiagnosis = "",
    KeyIssues = "",
    AbnormalFindings = "",
    CustomerInstructions = "",
    Reminders = "",
    sessionId = "",
    transcript = "",
    modificationCount = 0,
    blob = null,
    type = "outpatient",
    Summary = "",
    InstructionsStatus = "",
    RedFlags = "",
    OtherUpdates = "",
    Observation = "",
    consolidated = false,
    Protocols = "",
    ProgressNotes = "",
    PreSurgicalDocumentation = "",
    IntraSurgicalDocumentation = "",
    PostSurgicalDocumentation = "",
    OwnerCommunication = "",
    AdditionalDocumentation = "",
  }) => {
    const form = new FormData();
    form.append("subjective", Subjective);
    form.append("objective", Objective);
    form.append("assessment", Assessment);
    form.append("plan", Plan);
    form.append("conclusion", Conclusion);
    form.append("tag", DifferentialDiagnosis);
    form.append("keyIssues", KeyIssues);
    form.append("abnormalFindings", AbnormalFindings);
    form.append("customerInstructions", CustomerInstructions);
    form.append("reminders", Reminders);
    form.append("summary", Summary);
    form.append("instructionsStatus", InstructionsStatus);
    form.append("redFlags", RedFlags);
    form.append("otherUpdates", OtherUpdates);
    form.append("observation", Observation);
    form.append("sessionId", sessionId);
    form.append("modificationCount", modificationCount);
    form.append("type", type);
    form.append("consolidated", consolidated);
    form.append("preSurgicalDocumentation", PreSurgicalDocumentation);
    form.append("intraSurgicalDocumentation", IntraSurgicalDocumentation);
    form.append("postSurgicalDocumentation", PostSurgicalDocumentation);
    form.append("ownerCommunication", OwnerCommunication);
    form.append("additionalDocumentation", AdditionalDocumentation);
    if (consolidated) {
      form.append("protocols", Protocols);
      form.append("progressNotes", ProgressNotes);
    }
    if (transcript?.length > 0) form.append("transcript", transcript);
    dispatch(postClinicalNotesWoLoader({ appId: apntId, form })).then((res) => {
      if (res?.payload) {
        dispatch(
          getPetAndAppointmentDetailsById({
            petId,
            url: `${apntId ? `?appointmentId=${apntId}` : ""}`,
          }),
        );
        dispatch(
          showSnackBar({
            message: "Changes updated successfully!",
            type: "success",
          }),
        );
      }
    });
  };

  const onViewRedFlagUpdates = () => {
    setRedFlagUpdatesVsble(true);
  };

  const onViewDailyUpdates = () => {
    setDailyUpdatesVsble(true);
  };

  const onUpdateReviewed = (itm) => {
    dispatch(
      updateInpatientNoteReviewed({ noteId: itm?.noteId, reviewed: true }),
    ).then((res) => {
      if (res?.payload) {
        onApiCall();
        setDailyUpdatesVsble(false);
      }
    });
  };

  const onAssignUsers = () => {
    const assignedUsers =
      appDet?.assignedUsers?.length > 0
        ? appDet?.assignedUsers?.map((li) => {
            const matUser = clinicVets?.find((vl) => vl?.doctorId === li);
            return { id: li, name: matUser?.name, value: li };
          })
        : [];
    setModValues({ assignedUsers });
    setAssignUsersModVsble(true);
  };

  const getAllClinicUserListWoSelectedVet = (vetId = null) => {
    if (!vetId) return [];
    const reqVetList =
      clinicVets
        ?.filter(
          (vl) =>
            vl?.status === "Y" &&
            vl?.doctorId !== vetId &&
            vl?.userType?.some(
              (ut) => ut?.name === "ROLE_DOCTOR" || ut?.name === "ROLE_GROOMER",
            ),
        )
        ?.map((vet) => {
          return { name: vet?.name, value: vet?.doctorId, id: vet?.doctorId };
        }) ?? [];
    return reqVetList;
  };

  const onCloseAssignUsersMod = () => {
    setAssignUsersModVsble(false);
    setModValues({});
    setErrors({});
  };

  const onUpdateAssignedUsers = () => {
    const reqAssinUserIds =
      modValues?.assignedUsers?.length > 0
        ? (modValues?.assignedUsers?.map((li) => li?.id) ?? [])
        : [];
    dispatch(
      updateAppointmentAssignedUsers({
        apntId,
        data: { assignedUsers: reqAssinUserIds },
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseAssignUsersMod();
        onApiCall();
      }
    });
  };

  const onHandleNoShow = () => {
    setNoshowModVsble(true);
  };

  const onCloseNoShowMod = () => {
    setNoshowModVsble(false);
  };

  const onConfirmNoShow = () => {
    if (!appointmentId) return;
    dispatch(
      updateAppointmentStatusById({
        appointmentId: appointmentId,
        status: "noshow",
        successMsg: "No show added successfully!",
      }),
    ).then((res) => {
      if (res?.payload) {
        onCloseNoShowMod();
        onApiCall();
      }
    });
  };

  return (
    <>
      {isVideoCallEnabled ? (
        <div ref={myMeeting} className="vid-cal-con" />
      ) : null}

      <Grid container className="mv3 back-white pb100">
        {apntId ? (
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="mh10 mtMin10">
              <div className="appointmentBdrlft cardWHeader br8">
                <div className="bg-body bdrBtm flex-row-between-align-center pt10 pb10 pl15 pr15">
                  <Typography className="fs13 txt-semi-bold clr-dark-gray">
                    Appointment Details
                  </Typography>
                  {canManageDashboard &&
                  appDet?.appointmentStatus?.toLowerCase() !== "cancelled" ? (
                    <Typography
                      className="fs13 txt-semi-bold cursor blue-color"
                      onClick={() => setEditAppointment(true)}
                    >
                      Edit
                    </Typography>
                  ) : null}
                </div>
                <div className="gry-hor-split3" />
                <div className="pt10 pb10 pl15 pr15">
                  <div className="flex-row-between">
                    <div>
                      <div className="flex-row-ali-cen">
                        <Typography className="fs14 txt-semi-bold">
                          {`Meeting ${getRightSaluation(clinicVets, appDet?.doctorId)}
                  ${appDet?.doctorName}
                  ${
                    appDet?.facilityId && appDet?.facility
                      ? ` · Facility: ${appDet?.facility}`
                      : ""
                  }
                  `}
                        </Typography>
                      </div>
                      <div className="flex-row-ali-cen">
                        <Typography className="fs12 font-medium clr-dark-gray">
                          {`${appDet?.serviceType} · ${moment(appDet?.appointmentdateTime).format("DD MMM YYYY, HH:mm")}`}
                        </Typography>
                        {appDet?.inpatient ? (
                          <Typography className="fs12 font-medium red6 ml10">
                            No. of Days :{" "}
                            {getDayOffsetNumber(appDet?.appointmentdateTime)}
                          </Typography>
                        ) : null}
                      </div>
                    </div>
                    <div>
                      <div className="flex-row-ali-cen">
                        {inpatData?.inpatientStatus?.length > 0 ? (
                          <div
                            className={`txt-semi-bold fs10 white-color ph10 pv2 br5 capitalize ${inpatientStatusClass}`}
                          >
                            {inpatData?.inpatientStatus?.toLowerCase()}
                          </div>
                        ) : null}
                        <Typography
                          className={`${inpatData?.inpatientStatus?.length > 0 ? "ml10" : ""} font-medium fs10 pv2 ph10 white-color br5
                                    flex-center  ${
                                      appDet?.appointmentType === "Physical"
                                        ? "card-bot-blue-back"
                                        : "virtual-bg"
                                    }`}
                        >
                          {appDet?.appointmentType === "Home"
                            ? "Home Visit"
                            : appDet?.appointmentType}
                        </Typography>
                        <Typography
                          className={`ml10 fs10 txt-semi-bold white-color con-fit flex-center ph10 pv2 br5 ${getCardColorByApptStatus(
                            appDet?.appointmentStatus?.toLowerCase(),
                            appDet?.appointmentType,
                          )}`}
                        >
                          {getTypeByAppointmentType(
                            appDet?.appointmentStatus,
                            appDet?.appointmentType,
                          )}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className="flex-row-ali-cen mt5">
                    {canManageDashboard &&
                      appDet?.appointmentStatus?.toLowerCase() !==
                        "completed" &&
                      appDet?.appointmentStatus?.toLowerCase() !==
                        "cancelled" && (
                        <div>
                          <CustomButton
                            smallBtn
                            text="Assign Users"
                            onClick={onAssignUsers}
                          />
                        </div>
                      )}
                    {canManageDashboard &&
                      (appDet?.appointmentStatus?.toLowerCase() ===
                        "scheduled" ||
                        appDet?.appointmentStatus?.toLowerCase() ===
                          "inprogress" ||
                        appDet?.appointmentStatus?.toLowerCase() ===
                          "intransit") && (
                        <div className="ml10">
                          <CustomButton
                            smallBtn
                            text="Cancel"
                            redBtn
                            onClick={() => setCancelModVsble(true)}
                          />
                        </div>
                      )}
                    {canManageDashboard &&
                      (appDet?.appointmentStatus?.toLowerCase() ===
                        "consultation" ||
                        appDet?.appointmentStatus?.toLowerCase() ===
                          "finalize" ||
                        appDet?.appointmentStatus?.toLowerCase() ===
                          "completed") && (
                        <div className="ml10">
                          <CustomButton
                            smallBtn
                            text="Follow-up"
                            yellowBtn
                            onClick={handleFollowUp}
                          />
                        </div>
                      )}
                    {canManageDashboard &&
                      appDet?.appointmentType === "Physical" &&
                      (appDet?.appointmentStatus?.toLowerCase() ===
                        "scheduled" ||
                        appDet?.appointmentStatus?.toLowerCase() ===
                          "inprogress") && (
                        <div className="ml10">
                          <CustomButton
                            smallBtn
                            text="No Show"
                            onClick={onHandleNoShow}
                            yellowBtn
                          />
                        </div>
                      )}
                  </div>
                  {appDet?.inpatient ? (
                    <div className="flex-row-ali-cen mt5">
                      <Typography className="clr-dark-gray fs12 font-medium">
                        Admission notes:
                      </Typography>
                      <Typography className="fs12 txt-regular ml5">
                        {petAndApntData?.inpatientResponse?.remarks?.length > 0
                          ? petAndApntData?.inpatientResponse?.remarks
                          : "---"}
                      </Typography>
                    </div>
                  ) : null}
                  <div className="flex-row-ali-cen mt5">
                    <Typography className="clr-dark-gray fs12 font-medium">
                      Remarks:
                    </Typography>
                    <Typography className="fs12 txt-regular ml5">
                      {appDet?.remarks?.length > 0 ? appDet?.remarks : "---"}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="br8 cardWHeader bg-body flex-row-between pt10 pb10 pl15 pr15 mt15">
                <Typography className="fs13 txt-semi-bold clr-dark-gray flex-row-ali-cen flex-row-wrap">
                  Allergies:{" "}
                  {petAndApntData?.allergies?.length > 0 ? (
                    petAndApntData?.allergies?.map((al, i) => (
                      <Typography className="txt-semi-bold fs14 card-head-orange-color ml5">
                        {`${al?.allergies}${
                          i + 1 === petAndApntData?.allergies?.length ? "" : ","
                        }`}
                      </Typography>
                    ))
                  ) : (
                    <Typography className="txt-semi-bold fs14 card-head-orange-color ml5">
                      None specified
                    </Typography>
                  )}
                </Typography>
                {canManageDashboard && statusActive ? (
                  <EditIcon
                    className="cursor"
                    sx={addIcon}
                    onClick={() => setAllergyModOpen(true)}
                  />
                ) : null}
              </div>
            </div>
          </Grid>
        ) : null}

        {!appDet?.inpatient ? (
          <>
            <div className="w100Per mh10">
              <div className="cardWHeader br8 mt15">
                <div className="bg-body fs13 bdrBtm pt10 pb10 pl15 pr15">
                  <Typography className="fs13 txt-semi-bold clr-dark-gray">
                    Active Medications
                  </Typography>
                </div>
                <div className="flex-row-ali-cen ph10 pb10">
                  {getRequiredPresData()?.length > 0 ? (
                    getRequiredPresData()
                      ?.slice(0, 5)
                      ?.map((ml, i) => (
                        <>
                          <div className="w30Per">
                            <div className="flex-column">
                              <Tooltip title={ml?.name || ""}>
                                <Typography
                                  className="txt-semi-bold black2 fs12 capitalize"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {ml?.name}
                                </Typography>
                              </Tooltip>
                              {/* <Typography className="txt-semi-bold gray7 fs12 mt10">
                                    {ml?.name ?? ""}
                                  </Typography> */}
                              {ml?.date ? (
                                <div className="flex-row-ali-cen mt10">
                                  <div className="gray-dot2" />
                                  <Typography className="txt-semi-bold fs12 ml5 black2">
                                    Prescribed Date:{" "}
                                    {moment(ml?.date).format("DD MMM YYYY")}
                                  </Typography>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          {getRequiredPresData()?.slice(0, 5)?.length !==
                          i + 1 ? (
                            <div className="w5Per">
                              <div className="flex-center">
                                <div className="ver-bar-h30 h50" />
                              </div>
                            </div>
                          ) : null}
                        </>
                      ))
                  ) : (
                    <div className="no-rec h50">No records available</div>
                  )}
                </div>
              </div>
            </div>
            <div className="w100Per">
              <OverviewDiagnostics isBaseDetailsLoaded={isBaseDetailsLoaded} />
            </div>
          </>
        ) : null}

        {inpatData?.patientId && petAndApntData?.inpatientNotes ? (
          <>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mh10">
                <div className="cardWHeader br8 bg-body mt15 pt10 pb10 pl15 pr15">
                  <div className="flex-row-between">
                    <Typography className="fs13 txt-semi-bold clr-dark-gray">
                      Red Flags
                    </Typography>
                    <div className="flex-row-ali-cen">
                      {canManageDashboard ? (
                        <div
                          className="flex-row-ali-cen cursor"
                          onClick={() => handleAddUpdateRedAlert(null)}
                        >
                          <div className="flex-center plus-icon">
                            <AddIcon sx={addIcon} />
                          </div>
                          <Typography className="blue-color fs14 ml5 font-bold">
                            Add
                          </Typography>
                        </div>
                      ) : null}
                      {/* <Typography
                        className="blue-color fs12 txt-semi-bold underline cursor ml15"
                        onClick={onViewRedFlagUpdates}
                      >
                        View Updates
                      </Typography> */}
                    </div>
                  </div>
                  {[...(inpatientAlerts ?? [])]
                    ?.sort((a, b) => {
                      const aTime = new Date(
                        a?.updatedDate || a?.createdDate || 0,
                      )?.getTime();
                      const bTime = new Date(
                        b?.updatedDate || b?.createdDate || 0,
                      )?.getTime();
                      if (bTime !== aTime) return bTime - aTime;
                      return Number(b?.alertId ?? 0) - Number(a?.alertId ?? 0);
                    })
                    ?.map((alrt) => {
                      return (
                        <div
                          className="red-con w100Per mt15"
                          key={alrt?.alertId}
                        >
                          <div className="flex-row">
                            <div className="w95Per">
                              <Typography className="black28 fs12 txt-regular">
                                {alrt?.text}
                              </Typography>
                            </div>
                            <div className="w5Per flex1-end">
                              {canManageDashboard ? (
                                <img
                                  src={require("../../../../assets/images/png/edit-new.png")}
                                  alt=""
                                  className="img-wh20 cursor"
                                  onClick={() => handleAddUpdateRedAlert(alrt)}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Instructions />
            </Grid>

            {appDet?.inpatient ? (
              <div className="w100Per mh10 mt15">
                <div className="io-header-row">
                  <h2 className="io-section-title">Latest vitals</h2>
                  <div>
                    <CustomButton
                      text="Go to I/O Monitoring"
                      smallBtn
                      whiteBtn
                      onClick={goToIoMonitoring}
                    />
                  </div>
                </div>
                <div className="vitals-grid-row">
                  {[
                    {
                      key: "weight",
                      label: "WEIGHT",
                      value: latestVitalsSummary.weight,
                      showAsOf: true,
                    },
                    {
                      key: "temperature",
                      label: "TEMP",
                      value: latestVitalsSummary.temperature,
                      showAsOf: true,
                    },
                    {
                      key: "heartRate",
                      label: "HR",
                      value: latestVitalsSummary.heartRate,
                      showAsOf: true,
                    },
                    {
                      key: "respiratoryRate",
                      label: "RR",
                      value: latestVitalsSummary.respiratoryRate,
                      showAsOf: true,
                    },
                    {
                      key: "bodyConditionScore",
                      label: "BCS",
                      value: latestVitalsSummary.bodyConditionScore,
                      showAsOf: true,
                    },
                  ].map((card) => (
                    <div className="vital-card" key={card.key}>
                      <div className="vital-label">{card.label}</div>
                      <div
                        className={`vital-value ${
                          isAbnormalVital(card.key, card.value)
                            ? "temp-red"
                            : "navy-text"
                        }`}
                      >
                        {card.value}
                      </div>
                      {card.showAsOf ? (
                        <div className="vital-asof">
                          {latestVitalsSummary.asOfTime}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {appDet?.inpatient ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mh10 overview-tasks-section">
                  <div className="tp-header-row" style={{ marginBottom: 12 }}>
                    <h2 className="tp-section-title">Tasks</h2>
                    <Typography
                      className="fs13 txt-semi-bold blue-color cursor"
                      onClick={goToTreatmentPlanSchedule}
                    >
                      Full schedule →
                    </Typography>
                  </div>

                  <Typography className="fs11 txt-semi-bold gray4 mb10">
                    OVERDUE ({overdueTasks?.length ?? 0})
                  </Typography>
                  <div className="tp-schedule-list" style={{ marginBottom: 8 }}>
                    {(overdueTasks ?? [])
                      .slice(0, TASKS_PREVIEW_LIMIT)
                      .map((entry) => (
                        <div
                          key={entry.id}
                          className="tp-sched-row overdue-row"
                        >
                          <div className="tp-sched-time">{entry.time}</div>
                          <div className="tp-sched-name">{entry.name}</div>
                          <div className="tp-sched-actions">
                            <span className="badge-overdue">OVERDUE</span>
                            {canManageDashboard ? (
                              <div className="flex-row-ali-cen">
                                <div>
                                  <CustomButton
                                    text="Mark given"
                                    smallBtn
                                    onClick={() => markTaskGiven(entry)}
                                  />
                                </div>
                                <div className="ml10">
                                  <CustomButton
                                    text="Not Done"
                                    smallBtn
                                    redBtn
                                    onClick={() => handleTaskCancel(entry)}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                  </div>
                  {(overdueTasks?.length ?? 0) > TASKS_PREVIEW_LIMIT ? (
                    <Typography
                      className="fs12 gray4 cursor mb15"
                      sx={{ fontStyle: "italic" }}
                      onClick={goToTreatmentPlanSchedule}
                    >
                      {`+ ${overdueTasks.length - TASKS_PREVIEW_LIMIT} more overdue — see full schedule`}
                    </Typography>
                  ) : null}

                  <Typography className="fs11 txt-semi-bold gray4 mb10 mt10">
                    UPCOMING TODAY ({upcomingTasks?.length ?? 0})
                  </Typography>
                  <div className="tp-schedule-list" style={{ marginBottom: 8 }}>
                    {(upcomingTasks ?? [])
                      .slice(0, TASKS_PREVIEW_LIMIT)
                      .map((entry) => (
                        <div key={entry.id} className="tp-sched-row">
                          <div className="tp-sched-time">{entry.time}</div>
                          <div className="tp-sched-name">{entry.name}</div>
                          <div className="tp-sched-actions">
                            <span className="badge-due">Due</span>
                            {canManageDashboard ? (
                              <div className="flex-row-ali-cen">
                                <div>
                                  <CustomButton
                                    text="Mark Done"
                                    smallBtn
                                    onClick={() => markTaskGiven(entry)}
                                  />
                                </div>
                                <div className="ml10">
                                  <CustomButton
                                    text="Not Done"
                                    smallBtn
                                    redBtn
                                    onClick={() => handleTaskCancel(entry)}
                                  />
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))}
                  </div>
                  {(upcomingTasks?.length ?? 0) > TASKS_PREVIEW_LIMIT ? (
                    <Typography
                      className="fs12 gray4 cursor"
                      sx={{ fontStyle: "italic" }}
                      onClick={goToTreatmentPlanSchedule}
                    >
                      {`+ ${upcomingTasks.length - TASKS_PREVIEW_LIMIT} more scheduled — see full schedule`}
                    </Typography>
                  ) : null}

                  {(overdueTasks?.length ?? 0) === 0 &&
                  (upcomingTasks?.length ?? 0) === 0 ? (
                    <Typography className="fs13 gray4 mt5">
                      No tasks scheduled for today.
                    </Typography>
                  ) : null}
                </div>
              </Grid>
            ) : null}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <OverviewDiagnostics isBaseDetailsLoaded={isBaseDetailsLoaded} />
            </Grid>
          </>
        ) : null}
      </Grid>

      {followUpModVsble || isEditAppointment ? (
        <BookAnAppointment
          modalVisible={followUpModVsble || isEditAppointment}
          setBookModVisible={onCloseFollowUpMod}
          selectedPet={{
            petId: appDet?.petId,
            petName: appDet?.petName,
            petImage: appDet?.petImage,
            gender: appDet?.gender,
            breed: appDet?.breed,
            dob: appDet?.dob,
            userName: appDet?.userName,
            color: appDet?.color,
            weight: appDet?.weight,
            userMobile: appDet?.userMobile,
            mobile: appDet?.mobile,
          }}
          prevAppointmentId={appDet?.appointmentId}
          isEditAppointment={isEditAppointment}
          appointment={appDet}
          onBookModalCompleted={(e) => {
            if (e === "Api Success" && appDet?.appointmentId) {
              dispatch(
                getPetAndAppointmentDetailsById({
                  petId,
                  url: `${appDet?.appointmentId ? `?appointmentId=${appDet?.appointmentId}` : ""}`,
                }),
              );
            }
          }}
        />
      ) : null}

      {remarksModVsble ? (
        <UpdateRemarks
          remarksModVsble={remarksModVsble}
          onCloseRemarks={onCloseRemarks}
          remarks={petAndApntData?.appoinment?.remarks}
          notes={petAndApntData?.appointment?.notes}
          id={appointmentId}
          facilityId={appDet?.facilityId}
          facility={appDet?.facility}
        />
      ) : null}

      <CustomModal
        open={taskCancelModVsble}
        onClose={onCloseTaskCancelMod}
        header="Reason"
        modal
        modalWidth={30}
        headerShown={true}
      >
        <div className="ph10">
          <CustomTextField
            value={taskCancelRemarks}
            handleChange={(e) => setTaskCancelRemarks(e?.target?.value)}
            multiline
            fullWidth
          />
        </div>
        <div className="flex-center">
          <div className="w20Per mv20">
            <CustomButton
              text="Save"
              smallBtn
              onClick={onSaveTaskCancelRemarks}
            />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={bundleModVsble}
        onClose={handleBundleclose}
        header="Select Bundle"
        modal
        modalWidth={40}
      >
        <div className="scroll-60vh pr10">
          <CustomTextField
            placeholder="Search"
            fullWidth
            handleChange={(e) => setBundleSearchValue(e.target.value)}
            value={bundleSearchValue}
            backgroundColor={AppColors.lightPink}
          />

          {petTreatmentBundles?.length > 0 ? (
            (bundleSearchValue?.length > 0
              ? petTreatmentBundles?.filter((bl) =>
                  bl?.bundleName
                    ?.toLowerCase()
                    ?.includes(bundleSearchValue?.toLowerCase()),
                )
              : petTreatmentBundles
            )?.map((li, i) => {
              return (
                <div
                  key={i}
                  className={`mt15 cursor ${
                    li?.bundleId === selectedBundle?.bundleId
                      ? "blue-box2"
                      : "gray-box3"
                  }`}
                  onClick={() => setSelectedBundle(li)}
                >
                  <div className="flex-row-between-align-center">
                    <div className="flex-row-ali-cen">
                      <CustomCheckbox
                        circle
                        checked={
                          selectedBundle
                            ? li?.bundleId === selectedBundle?.bundleId
                            : false
                        }
                      />
                      <Typography className="fs14 blue-color font-bold">
                        {li?.bundleName}
                      </Typography>
                    </div>
                    <Typography className="font-bold fs14 red12">
                      Rs {Number(li?.estimatedCost)?.toFixed(2)}
                    </Typography>
                  </div>
                  <div className="flex-row-ali-cen ph10">
                    <div className="gry-dot2" />
                    <Typography className="fs12 blue-color txt-regular ml5">
                      Type:
                    </Typography>
                    <Typography className="fs12 black20 txt-regular ml5">
                      {li?.usageType}
                    </Typography>
                  </div>

                  <Typography className="gray22 fs14 font-medium ph10">
                    {li?.description}
                  </Typography>
                </div>
              );
            })
          ) : (
            <div className="no-record">No active bundles available</div>
          )}
          {selectedBundle ? (
            <div className="flex-center">
              <div className="mv20">
                <CustomButton smallBtn text="Assign" onClick={handleAssign} />
              </div>
            </div>
          ) : null}
        </div>
      </CustomModal>

      <CustomModal
        open={cancelModVsble}
        onClose={onCancelModClose}
        header="Cancel"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to cancel the appointment?
        </Typography>
        <div className="ml10 mt15">
          <CustomTextField
            label="Reason for cancellation"
            placeholder="Enter reason for cancellation (optional)"
            multiline
            rows={4}
            value={cancelReason}
            handleChange={(e) => setCancelReason(e.target.value)}
            fullWidth
          />
        </div>
        <div className="flex-center">
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onConfirmCancel}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton
                text="No"
                onClick={() => {
                  onCancelModClose();
                  setCancelReason("");
                }}
                smallBtn
              />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={noShowModVsble}
        onClose={onCloseNoShowMod}
        header="No Show"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to mark the appointment as no-show?
        </Typography>

        <div className="flex-center">
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onConfirmNoShow}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseNoShowMod} smallBtn />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={reModVsble}
        onClose={onReModClose}
        header="Reschedule / Reassign"
        rightModal
        modalWidth={30}
        headerBold
      >
        <Grid container spacing={1} className="ph20 scroll-80vh">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Card sx={cardSty} className="CustomCard-back-appointment">
              <div className="maint">
                <div className="flex-row">
                  <Grid item xs={3}>
                    <div className="flex-center">
                      {appDet?.petImage ? (
                        <CardMedia
                          image={appDet?.petImage}
                          className="CustomCard-img3-bill"
                        />
                      ) : (
                        <div className="flex-center h50img">
                          <Typography className="font-bold fs30 white-color capitalize">
                            {appDet?.petName?.[0]}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Grid>
                  <div className="flex-row">
                    <div className="flex-start">
                      <div className="flex-column">
                        <div className="flex-row">
                          <div className="mb10 txt-semi-bold black fs14 capitalize">
                            {appDet?.petName}
                          </div>
                          <Typography
                            className={`ml5 text capitalize ${
                              appDet?.gender === "male"
                                ? "card-blue-color"
                                : "card-rose-color"
                            }`}
                          >
                            {`(${appDet?.gender})`}
                          </Typography>
                        </div>
                        <Typography className="mb10 txt-regular card-gray-color fs12">
                          {appDet?.breed}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex-row parentcontainer mt10">
                    <div className="flex-row iconcontainer">
                      <AccountCircleOutlinedIcon
                        sx={{ width: 25, height: 25 }}
                      />
                      <div className="txt-semi-bold fs14 capitalize">
                        {appDet?.userName}
                      </div>
                    </div>
                    <div className="meeting-doctor">
                      {moment(appDet?.appointmentDate).fromNow()} (
                      {appDet?.appointmentDate})
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <div className="txt-semi-bold fs14 light-grey">
              Booking Type <span className="red-color">*</span>
            </div>
            <Select value={modValues?.bookingType} selectFixed />
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
            <div className="txt-semi-bold fs14 light-grey">
              Consultation Mode <span className="red-color">*</span>
            </div>
            <Select value={appDet?.appointmentType} selectFixed />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="txt-semi-bold fs14 light-grey">
              Service Type <span className="red-color">*</span>
            </div>
            <Select value={appDet?.serviceType} selectFixed />
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="txt-semi-bold fs14 light-grey">
              Reason for visit <span className="red-color">*</span>
            </div>
            <Select value={appDet?.reason} selectFixed />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Select
              list={vets}
              handleChange={(e, value) => onChangeValue("vetName", value)}
              name="vetName"
              search
              value={modValues?.vetName}
              error={!!errors?.vetName}
              helperText={errors?.vetName}
              label="Vet Name *"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Select
              list={vets}
              handleChange={(e) =>
                onChangeValue("assignedUsers", e?.target?.value)
              }
              name="assignedUsers"
              value={modValues?.assignedUsers ?? []}
              error={!!errors?.assignedUsers}
              helperText={errors?.assignedUsers}
              label="Assigned Users"
              multiSelectSearchCheck
            />
          </Grid>
          {appDet?.appointmentStatus?.toLowerCase() === "scheduled" && (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="txt-semi-bold fs14 light-grey">
                Appointment Time:{" "}
                {moment(new Date(appDet?.appointmentdateTime)).format(
                  "DD MMM YYYY HH:mm",
                )}
              </div>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            className="mbminus"
          >
            <div className="flex-row">
              <div className="txt-semi-bold fs14 light-grey mt10">
                Appointment <span className="red-color">*</span> :
              </div>
              <div className="ml5">
                <CustomCheckbox
                  radio
                  value={modValues?.appointmentOption}
                  onChange={(e) =>
                    onChangeValue("appointmentOption", e?.target?.value)
                  }
                  radios={
                    appDet?.appointmentStatus?.toLowerCase() !== "scheduled"
                      ? appointmentList
                      : appointmentLaterList
                  }
                  disabled={
                    appDet?.appointmentStatus?.toLowerCase() !== "scheduled"
                  }
                  defaultValue={modValues?.appointmentOption}
                />
              </div>
            </div>

            {!!errors?.appointmentOption && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography color="error" className="mt5 fs14">
                  Appointment is Required
                </Typography>
              </Grid>
            )}
          </Grid>

          {appDet?.appointmentStatus?.toLowerCase() === "scheduled" &&
            modValues?.appointmentOption === "Later" && (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-mont fs14 ">
                  Date <span className="red-color">*</span>
                </div>
                <div className="mb20">
                  <CustomTextField
                    name="date"
                    value={modValues?.date}
                    labelTop
                    fullWidth
                    handleChange={(e) =>
                      onChangeValue("date", e?.target?.value)
                    }
                    mobileDateWithBatch
                    // markDateList={holidayDates ?? []}
                  />
                </div>
              </Grid>
            )}

          {appDet?.appointmentStatus?.toLowerCase() === "scheduled" &&
            modValues?.appointmentOption === "Later" &&
            modValues?.date &&
            getRequiredSlotList({
              availability: vetAvailability,
              date: modValues?.date,
              bookedApnts: vetBookedApnts,
              holidays,
            })?.length > 0 &&
            getRequiredSlotList({
              availability: vetAvailability,
              date: modValues?.date,
              bookedApnts: vetBookedApnts,
              holidays,
            })?.map((item, i) => {
              return (
                <div
                  key={i}
                  onClick={() => handleSelectTime(item)}
                  className={
                    selectTime === item?.startTime
                      ? "bgContainerSelected cursor"
                      : "bgContainerUnselected cursor"
                  }
                >
                  {item?.startTime}
                </div>
              );
            })}

          {selectTimeIdError && (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography color="error" className="mt5 fs14">
                Time is Required
              </Typography>
            </Grid>
          )}
          <div className="flex1-end mt20">
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <CustomButton text="Save" onClick={onSaveRe} smallBtn />
            </Grid>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={movPatModVsble}
        onClose={onCloseModPatMod}
        header="Move Patient"
        modal
        modalWidth={30}
        headerBold
      >
        <Grid container spacing={1} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex-row-ali-cen">
              <div className="w25Per">
                <Typography className="txt-semi-bold gray7 fs14">
                  Move To
                </Typography>
              </div>
              <div className="w75Per">
                <Select
                  list={movePatientList}
                  handleChange={(e) =>
                    onChangeMovePat("moveTo", e?.target?.value)
                  }
                  name="moveTo"
                  select
                  value={modValues?.moveTo}
                  label=""
                />
              </div>
            </div>
          </Grid>
          <div className="flex1-end mt20 mb10">
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <CustomButton text="Submit" onClick={onMovePatient} smallBtn />
            </Grid>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={redAlertModVsble}
        onClose={onCloseRedAlertMod}
        header="Red Flag"
        modal
        modalWidth={40}
        headerBold
      >
        <Grid container spacing={1} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <CustomTextField
              name="alert"
              fullWidth
              handleChange={(e) =>
                setModValues({ ...modValues, alert: e?.target?.value })
              }
              value={modValues?.alert}
              multiline
            />
          </Grid>
          <div className="flex1-end mt20 mb10">
            <div>
              <CustomButton text="Submit" onClick={onSaveRedAlert} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={redFlagUpdatesVsble}
        onClose={() => setRedFlagUpdatesVsble(false)}
        header="Red Flags"
        modal
        modalWidth={40}
        headerCenter
      >
        <div className="scroll-50vh">
          {petAndApntData?.inpatientNotes?.filter(
            (li) =>
              li?.appointmentId === appointmentId &&
              li?.type?.toLowerCase() === "inpatient" &&
              li?.redFlags?.length > 0,
          )?.length > 0 ? (
            <div className="timeline-container">
              <div className="timeline-line" />
              <div className="tasks-container">
                {petAndApntData?.inpatientNotes
                  ?.filter(
                    (li) =>
                      li?.appointmentId === appointmentId &&
                      li?.type?.toLowerCase() === "inpatient" &&
                      li?.redFlags?.length > 0,
                  )
                  ?.map((li) => (
                    <div key={li?.noteId}>
                      <div className="flex-row mv10">
                        <div className="tsk-gry-dot" />
                        <div className="gray37 fs12 txt-semi-bold con-fit ml20 tsk-gry-time">
                          {moment(li?.createdDate).format("DD MMM YYYY, HH:mm")}
                        </div>
                      </div>
                      <Typography className="ml20 txt-semi-bold fs14 black7 mt10">
                        {li?.redFlags}
                      </Typography>
                      <Typography className="ml20 font-medium fs12 gray11 mt5">
                        {li?.userName}
                      </Typography>
                    </div>
                  ))}
              </div>
            </div>
          ) : (
            <div className="no-rec">No records available</div>
          )}
        </div>
      </CustomModal>

      <CustomModal
        open={dailyUpdatesVsble}
        onClose={() => setDailyUpdatesVsble(false)}
        header="Daily Updates"
        modal
        modalWidth={80}
        headerCenter
      >
        <div className="scroll-60vh">
          {petAndApntData?.inpatientNotes
            ?.filter(
              (li) =>
                li?.appointmentId === appointmentId &&
                li?.type?.toLowerCase() === "inpatient" &&
                li?.consolidated === true,
            )
            ?.map((li, i) => {
              return (
                <div key={li?.noteId} className="mv10">
                  <Typography className="txt-semi-bold fs14 blue-color mt10">
                    Note {i + 1}
                  </Typography>
                  {inpatientList?.map((lii) => {
                    return (
                      <div>
                        <div className="heading-fw600 fs14 black mt5">
                          {nameExten?.[lii]}
                        </div>
                        <Typography className="font-medium fs14 gray9">
                          {li?.[lii?.charAt(0)?.toLowerCase() + lii?.slice(1)]}
                        </Typography>
                      </div>
                    );
                  })}
                  <div className="flex-center mv10">
                    <div>
                      <CustomButton
                        text="Reviewed"
                        smallBtn
                        onClick={() => onUpdateReviewed(li)}
                        disabled={li?.reviewed}
                        grayBtn={li?.reviewed}
                      />
                    </div>
                  </div>
                  <Divider className="mv10" />
                </div>
              );
            })}
        </div>
      </CustomModal>

      <CustomModal
        open={assignUsersModVsble}
        onClose={onCloseAssignUsersMod}
        header="Assign Users"
        modalWidth={30}
        modal
      >
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Select
              list={getAllClinicUserListWoSelectedVet(appDet?.doctorId)}
              value={modValues?.assignedUsers ?? []}
              name="assignedUsers"
              handleChange={(e) => {
                onChangeValue("assignedUsers", e?.target?.value);
              }}
              label="Assigned Users"
              multiSelectSearchCheck
            />
          </Grid>
          <div className="flex1-center mv20">
            <div>
              <CustomButton
                text="Update"
                onClick={onUpdateAssignedUsers}
                smallBtn
              />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </>
  );
};

export default Overview;
